import { CustomElement } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { validatorEntry } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { parseMinutesIntervals, stringifyMinutesIntervals } from "@cervval.js/common/src/tools/interval.string.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { EditorOptions, defaultEditorOptions } from "../editor";
import { CjsInputTextEditor } from "../inputTextEditor/inputTextEditor";
import { MinutesIntervalsValidator } from "./minutesIntervalsEditor.validators";

export type MinutesIntervalsEditorOptions = EditorOptions & {
    /** 
     * Convert only for 24h "day" duration: 
     * formatMinutesHHMM(1520) => "25:20" 
     * formatMinutesHHMM(1520, {dayFormat: true}) => "01:20" 
     * */
    dayFormat?: boolean
};

export const defaultMinutesIntervalsEditorOptions: MinutesIntervalsEditorOptions = {
    ...defaultEditorOptions
};

@CustomElement({
    name: "cjs-minutes-intervals-editor",
    elementCssClasses: ["cjs-editor", ".cjs-input-text-editor"]
})
export class CjsMinutesIntervalsStringEditor extends CjsInputTextEditor {

    /** Attributes options of MinutesIntervalsEditor */
    @TypedWebComponentOptions(defaultMinutesIntervalsEditorOptions)
    public override readonly options!: WebComponentOptions<MinutesIntervalsEditorOptions>;

    /** 
     * Format string value by parsing and stringify minutes intervals
     * Ex: 6-14;22-6 =>  06:00-14:00;22:00-06:00 
     */
    private _formatInputValue(value: string | undefined): typeof value {
        if (!value) { return value; }
        const intervals = parseMinutesIntervals(value);
        return stringifyMinutesIntervals(intervals, { dayFormat: this.options.dayFormat ?? false });
    }

    /** @override */
    protected override _onInputValueChange(event?: Event | anyReallyAny): void {
        let newValue = this._getValueFromInputComponent();
        if (this._validateInputValue(newValue)) {
            newValue = this._formatInputValue(newValue);
            this.setValue(newValue);
        }
    }

    /**
     * Decorate the input depending on input value validity
     * @returns false if value is invalid
     */
    protected override _validateInputValue(value: string | undefined): boolean {
        if (!super._validateInputValue(value)) {
            return false;
        }

        const valuePropertyConfig: PropertyConfig = {
            propertyName: "minutesIntervalsValue",
            label: "",
            typeUid: PropertyConfigType.MINUTES_INTERVALS,
            validators: {
                ...validatorEntry(MinutesIntervalsValidator, undefined)
            }
        };


        const propsValues: Record<string, unknown> = {
            "value": value ?? this._value
        };
        const validatedProps: Record<string, PropertyConfig> = {
            "value": valuePropertyConfig
        };

        const errors = this._validation.validate(validatedProps, propsValues);
        this.setValidationError(errors["value"]);

        // Check if no error given === valid value
        return Object.keys(errors).length === 0;
    }

}